import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Search, ScrollText } from "lucide-react";

const ACTION_COLOR = {
  SCREENING_CREATED: "text-blue-300 border-blue-800 bg-blue-950/40",
  REVIEW_DECISION: "text-amber-300 border-amber-800 bg-amber-950/40",
  EXPORT_JSON: "text-slate-300 border-slate-700 bg-slate-950/40",
  EXPORT_PDF: "text-slate-300 border-slate-700 bg-slate-950/40",
  SANCTIONS_REFRESH: "text-emerald-300 border-emerald-800 bg-emerald-950/40",
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/audit-logs", { params: q ? { q } : {} }).then((r) => setRows(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
          Immutable Audit Trail
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Append-only log of every screening, model output, export and human decision.
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <input
          data-testid="audit-search-input"
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 pl-9 text-sm text-slate-100 outline-none focus:border-blue-500"
          placeholder="Search actor or entity…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
        />
      </div>

      <div className="relative border-l border-slate-800 pl-6" data-testid="audit-timeline">
        {rows.length === 0 && (
          <div className="flex flex-col items-center py-16 text-slate-500">
            <ScrollText className="mb-3 h-10 w-10 text-slate-700" />
            No audit events yet.
          </div>
        )}
        {rows.map((e) => (
          <div key={e.id} data-testid={`audit-event-${e.id}`} className="relative mb-4">
            <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full border-2 border-slate-950 bg-blue-500" />
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span
                  className={`rounded-md border px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wide ${
                    ACTION_COLOR[e.action] || "text-slate-300 border-slate-700 bg-slate-950/40"
                  }`}
                >
                  {e.action}
                </span>
                <span className="font-mono text-[10px] text-slate-500">{e.timestamp}</span>
              </div>
              <div className="mt-2 text-sm text-slate-300">
                <span className="font-medium text-slate-100">{e.actor_email}</span>
                <span className="text-slate-500"> ({e.actor_role})</span>
                {e.details?.entity_name && (
                  <span> — {e.details.entity_name}</span>
                )}
              </div>
              {(e.details?.verdict || e.details?.decision) && (
                <div className="mt-1 font-mono text-xs text-slate-400">
                  {e.details.verdict && `verdict=${e.details.verdict} score=${e.details.risk_score}`}
                  {e.details.decision && `decision=${e.details.decision} → ${e.details.new_status}`}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
